import React from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  AppBar,
  Toolbar,
  Grid,
  Card,
  CardContent,
  Stack,
  Paper,
  useTheme,
  useMediaQuery,
  Chip,
  Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  People as PeopleIcon,
  Description as DescriptionIcon,
  Work as WorkIcon,
  Event as EventIcon,
  Inventory as InventoryIcon,
  Payment as PaymentIcon,
  CalendarToday as CalendarIcon,
  CloudUpload as CloudIcon,
  Group as GroupIcon,
  PictureAsPdf as PdfIcon,
  CheckCircle as CheckCircleIcon,
  Print as PrintIcon,
  Assignment as AssignmentIcon,
  AutoAwesome as AutoAwesomeIcon,
  Draw as DrawIcon,
  Schedule as ScheduleIcon,
  Lock as LockIcon,
  Receipt as ReceiptIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  ArrowForward as ArrowForwardIcon,
  Build as BuildIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Send as SendIcon
} from '@mui/icons-material';

export default function Landing() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const scrollToFeatures = () => {
    const featuresSection = document.getElementById('features');
    if (featuresSection) {
      featuresSection.scrollIntoView({ behavior: 'smooth' });
    }
  };


  const features = [
    {
      icon: <CalendarIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
      title: 'Smart Scheduling',
      description: 'Visual calendar with drag-and-drop. Week and month views. Color-coded by job type. Prevent double-booking.'
    },
    {
      icon: <LockIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
      title: 'Customer Portal',
      description: 'Secure portal for customers to view estimates, sign digitally, view invoices, and pay online—no account needed.'
    },
    {
      icon: <PaymentIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
      title: 'Online Payments',
      description: 'Accept payments directly through Stripe. Customers pay invoices online. Track payment status in real-time.'
    },
    {
      icon: <DrawIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
      title: 'Digital Signatures',
      description: 'Capture signatures on-site or remotely. Signatures embedded in PDFs. Auto-approve estimates upon signature.'
    },
    {
      icon: <AutoAwesomeIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
      title: 'Smart Automation',
      description: 'Auto-send reminders, follow-ups, and invoices. Auto-create jobs from approved estimates. Set it and forget it.'
    },
    {
      icon: <AssignmentIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
      title: 'Inventory Tracking',
      description: 'Numbered pick lists (PL-001, PL-002). Track check-ins/outs. Know exactly where every tool and material is.'
    },
    {
      icon: <PdfIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
      title: 'Professional PDFs',
      description: 'Branded PDFs with your logo, company info, and terms. Generate estimates, invoices, and schedules instantly.'
    },
    {
      icon: <GroupIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
      title: 'Team Management',
      description: 'Organize crews and assign work. Track who\'s working where. Manage roles and permissions.'
    },
    {
      icon: <CloudIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
      title: 'Cloud-Based',
      description: 'Access from anywhere. Real-time sync across devices. Dark mode support. Mobile-responsive design.'
    }
  ];

  const benefits = [
    { text: 'Never double-book a crew again', icon: <CheckCircleIcon /> },
    { text: 'Know exactly where every tool and material is', icon: <CheckCircleIcon /> },
    { text: 'Get signatures and payments faster', icon: <CheckCircleIcon /> },
    { text: 'Stop losing estimates and invoices', icon: <CheckCircleIcon /> },
    { text: 'Automate repetitive tasks', icon: <CheckCircleIcon /> },
    { text: 'Professional branding on every document', icon: <CheckCircleIcon /> }
  ];

  const jobFlowSteps = [
    {
      icon: <PeopleIcon sx={{ fontSize: 32 }} />,
      title: 'Lead comes in',
      description: 'Add a customer in 10 seconds, or import from your existing list.',
      color: theme.palette.primary.main || '#1976d2'
    },
    {
      icon: <DescriptionIcon sx={{ fontSize: 32 }} />,
      title: 'Send an estimate',
      description: 'Build it from your saved items, send a link, they can sign on their phone.',
      color: '#8B5CF6'
    },
    {
      icon: <ScheduleIcon sx={{ fontSize: 32 }} />,
      title: 'Schedule the work',
      description: 'Site Sync creates the job, you drop it on the calendar and assign your crew.',
      color: '#F59E0B'
    },
    {
      icon: <AccountBalanceWalletIcon sx={{ fontSize: 32 }} />,
      title: 'Get paid & wrap up',
      description: 'Log cash/check or let them pay online. Mark job done, move to the next.',
      color: '#10B981'
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Navigation Bar */}
      <AppBar 
        position="sticky" 
        color="transparent" 
        elevation={0} 
        sx={{ 
          bgcolor: 'background.paper', 
          borderBottom: 1, 
          borderColor: 'divider',
          backdropFilter: 'blur(10px)',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          ...(theme.palette.mode === 'dark' && {
            backgroundColor: 'rgba(18, 18, 18, 0.8)'
          })
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 2, sm: 0 } }}>
            <Typography
              variant="h5"
              component="div"
              sx={{
                flexGrow: 1,
                fontWeight: 700,
                color: 'primary.main',
                cursor: 'pointer',
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                letterSpacing: '-0.02em'
              }}
              onClick={() => navigate('/')}
            >
              Site Sync
            </Typography>
            <Stack direction="row" spacing={{ xs: 1, sm: 2 }} alignItems="center">
              <Button 
                color="inherit" 
                onClick={scrollToFeatures}
                sx={{ 
                  display: { xs: 'none', sm: 'inline-flex' },
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                Features
              </Button>
              <Button 
                color="inherit" 
                onClick={() => navigate('/login')}
                sx={{ 
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                Login
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/signup')}
                sx={{ 
                  ml: { xs: 0, sm: 1 },
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  borderRadius: 2
                }}
              >
                Get Started Free
              </Button>
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, sm: 8, md: 12 }, px: { xs: 2, sm: 3 } }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Chip 
              label="Join the Beta" 
              color="primary" 
              size="small"
              sx={{ mb: 3, fontWeight: 600 }}
            />
            <Typography
              variant={isMobile ? 'h3' : 'h2'}
              component="h1"
              fontWeight={800}
              gutterBottom
              sx={{ 
                mb: 3,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
                lineHeight: 1.1,
                letterSpacing: '-0.03em'
              }}
            >
              Stop Managing Jobs with Spreadsheets
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ 
                mb: 4, 
                lineHeight: 1.7,
                fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                fontWeight: 400
              }}
            >
              All-in-one platform for contractors to manage estimates, jobs, schedules, inventory, and payments. Built for the real world.
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ mb: 4 }}
            >
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => navigate('/signup')}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                  boxShadow: 3,
                  '&:hover': {
                    boxShadow: 6
                  }
                }}
              >
                Start Free Beta
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={scrollToFeatures}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2
                  }
                }}
              >
                See How It Works
              </Button>
            </Stack>
            <Stack direction="row" spacing={4} sx={{ flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h4" fontWeight={700} color="primary.main">
                  100%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Cloud-Based
                </Typography>
              </Box>
              <Box>
                <Typography variant="h4" fontWeight={700} color="primary.main">
                  Free
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Beta Access
                </Typography>
              </Box>
              <Box>
                <Typography variant="h4" fontWeight={700} color="primary.main">
                  5 Min
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Setup Time
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                position: 'relative',
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                border: '1px solid',
                borderColor: 'divider',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 30px 80px rgba(0,0,0,0.4)'
                },
                bgcolor: 'background.paper'
              }}
            >
              {/* Dashboard Screenshot Image */}
              <Box
                component="img"
                src="/dashboard-screenshot.png"
                alt="Site Sync Dashboard - Manage jobs, schedules, inventory, and payments all in one place"
                onError={(e) => {
                  // If image doesn't load, show error message
                  console.warn('Dashboard screenshot not found. Please add dashboard-screenshot.png to the public folder.');
                  e.target.style.display = 'none';
                  if (e.target.nextSibling) {
                    e.target.nextSibling.style.display = 'flex';
                  }
                }}
                sx={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  borderRadius: 3,
                  objectFit: 'contain'
                }}
              />
              {/* Fallback message if image doesn't exist */}
              <Box
                sx={{
                  display: 'none',
                  minHeight: { xs: 300, md: 480 },
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  p: 4,
                  textAlign: 'center',
                  bgcolor: 'background.paper',
                  borderRadius: 3
                }}
              >
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Dashboard Preview
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add dashboard-screenshot.png to the public folder to see the preview
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Features Grid Section */}
      <Box id="features" sx={{ py: { xs: 6, sm: 8, md: 12 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h3"
              component="h2"
              fontWeight={700}
              gutterBottom
              sx={{ mb: 2 }}
            >
              Everything You Need
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: 600, mx: 'auto' }}
            >
              Powerful features built specifically for construction and service businesses
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    p: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  <Box sx={{ mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
                    {feature.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Job Flow Section */}
      <Box sx={{ bgcolor: 'background.paper', py: { xs: 6, sm: 8, md: 12 } }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h3"
              component="h2"
              fontWeight={700}
              gutterBottom
              sx={{ mb: 2 }}
            >
              The Complete Job Flow
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: 700, mx: 'auto' }}
            >
              From estimate to payment—everything connected in one seamless workflow
            </Typography>
          </Box>

          {/* Desktop Flow - Horizontal 4-Step Line */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center', alignItems: 'flex-start', gap: 3, flexWrap: 'nowrap' }}>
            {jobFlowSteps.map((step, index) => (
              <React.Fragment key={index}>
                <Box sx={{ flex: '1 1 0', minWidth: 0, maxWidth: '280px' }}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 3.5,
                      borderRadius: 3,
                      border: '2px solid',
                      borderColor: 'divider',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: 8,
                        borderColor: step.color
                      },
                      height: '100%',
                      minHeight: 260,
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        bgcolor: `${step.color}20`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2.5,
                        border: `2px solid ${step.color}50`
                      }}
                    >
                      <Box sx={{ color: step.color }}>
                        {step.icon}
                      </Box>
                    </Box>
                    <Typography 
                      variant="h5" 
                      fontWeight={700} 
                      gutterBottom 
                      sx={{ 
                        mb: 1.5, 
                        fontSize: '1.25rem',
                        color: 'text.primary',
                        lineHeight: 1.3
                      }}
                    >
                      {step.title}
                    </Typography>
                    <Typography 
                      variant="body1" 
                      color="text.secondary" 
                      lineHeight={1.7} 
                      sx={{ 
                        fontSize: '0.9375rem',
                        flexGrow: 1
                      }}
                    >
                      {step.description}
                    </Typography>
                  </Paper>
                </Box>
                {index < jobFlowSteps.length - 1 && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mt: 3,
                    flexShrink: 0,
                    px: 1
                  }}>
                    <ArrowForwardIcon sx={{ color: theme.palette.primary.main, fontSize: 32, opacity: 0.7 }} />
                  </Box>
                )}
              </React.Fragment>
            ))}
          </Box>

          {/* Mobile/Tablet Flow - Vertical */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            <Stack spacing={3}>
              {jobFlowSteps.map((step, index) => (
                <React.Fragment key={index}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 3.5,
                      borderRadius: 3,
                      border: '2px solid',
                      borderColor: 'divider',
                      borderLeft: `4px solid ${step.color}`
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: 2,
                          bgcolor: `${step.color}20`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          border: `2px solid ${step.color}50`
                        }}
                      >
                        <Box sx={{ color: step.color }}>
                          {step.icon}
                        </Box>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: 'text.primary', fontSize: '1.125rem', mb: 1.5 }}>
                          {step.title}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" lineHeight={1.7} sx={{ fontSize: '0.9375rem' }}>
                          {step.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                  {index < jobFlowSteps.length - 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <ArrowForwardIcon 
                        sx={{ 
                          color: theme.palette.primary.main, 
                          fontSize: 28,
                          transform: 'rotate(90deg)',
                          opacity: 0.7
                        }} 
                      />
                    </Box>
                  )}
                </React.Fragment>
              ))}
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ bgcolor: 'background.default', py: { xs: 6, sm: 8, md: 12 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h3"
                component="h2"
                fontWeight={700}
                gutterBottom
              >
                Built for Real Contractors
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 4, lineHeight: 1.8, fontSize: '1.125rem' }}
              >
                Whether you're a GC managing multiple crews, a remodeler tracking estimates and change orders, or a tradesperson coordinating materials and schedules—Site Sync has the tools you actually need.
              </Typography>
              <Stack spacing={2.5}>
                {benefits.map((benefit, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ 
                      color: 'primary.main', 
                      mt: 0.5,
                      '& svg': { fontSize: 24 }
                    }}>
                      {benefit.icon}
                    </Box>
                    <Typography variant="body1" sx={{ fontSize: '1.0625rem', lineHeight: 1.6 }}>
                      {benefit.text}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={4}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  bgcolor: 'primary.main',
                  color: 'white',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                }}
              >
                <Typography variant="h4" fontWeight={700} gutterBottom sx={{ mb: 3 }}>
                  Perfect For:
                </Typography>
                <Grid container spacing={2}>
                  {['General Contractors', 'Remodelers', 'HVAC', 'Electrical', 'Plumbing', 'Roofing', 'Flooring', 'Landscaping'].map((trade, index) => (
                    <Grid item xs={6} key={index}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 20, opacity: 0.9 }} />
                        <Typography variant="body1" fontWeight={500}>
                          {trade}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Final CTA Section */}
      <Box sx={{ 
        py: { xs: 8, sm: 10, md: 12 }, 
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 }, position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h3"
              component="h2"
              fontWeight={700}
              gutterBottom
              sx={{ mb: 3, color: 'white' }}
            >
              Ready to Get Started?
            </Typography>
            <Typography
              variant="h6"
              sx={{ mb: 5, opacity: 0.95, color: 'white', fontWeight: 400, lineHeight: 1.7 }}
            >
              Join contractors who ditched the spreadsheets and are managing crews, jobs, and materials the modern way.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/signup')}
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                px: 6,
                py: 2,
                fontSize: '1.125rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                boxShadow: 4,
                mb: 2,
                '&:hover': {
                  bgcolor: 'grey.100',
                  boxShadow: 6
                }
              }}
            >
              Start Free Beta
            </Button>
            <Typography
              variant="body2"
              sx={{ mt: 3, opacity: 0.9, color: 'white' }}
            >
              No credit card required • Setup in 5 minutes • We'll help you get started
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider', py: { xs: 4, sm: 5 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" fontWeight={700} color="primary.main" gutterBottom>
                Site Sync
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                All-in-one job, schedule, and inventory management for contractors.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Currently in beta. We're actively improving based on your feedback.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={2} 
                justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                alignItems={{ xs: 'flex-start', md: 'center' }}
              >
                <Button 
                  color="inherit" 
                  size="small" 
                  onClick={() => navigate('/login')}
                  sx={{ textTransform: 'none' }}
                >
                  Login
                </Button>
                <Button 
                  color="inherit" 
                  size="small" 
                  onClick={() => navigate('/signup')}
                  sx={{ textTransform: 'none' }}
                >
                  Sign Up
                </Button>
              </Stack>
            </Grid>
          </Grid>
          <Divider sx={{ my: 4 }} />
          <Box>
            <Typography variant="body2" color="text.secondary" align="center">
              © {new Date().getFullYear()} Site Sync. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}